import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../context/AppContext';

interface PasswordConfirmProps {
  /**
   * the area is unmounted while closed so the password field cannot be filled
   * by an autofill run the user never sees
   */
  open: boolean;
  /**
   * unique per confirmation on the page - it becomes the id of the input and
   * the htmlFor of its label. several passkey rows can be open at once, so a
   * shared id would silently detach every label but the first
   */
  id: string;
  /**
   * already translated question, e.g. "Diesen Passkey wirklich löschen?"
   */
  question: string;
  /**
   * already translated label of the executing button
   */
  confirmLabel: string;
  /**
   * false for the actions the backend does not protect with a password
   * (forgetting trusted devices) - the confirmation step itself stays
   */
  requirePassword?: boolean;
  /**
   * blocks a second submit while the first one is still running
   */
  busy?: boolean;
  onConfirm: (password: string) => void;
  onCancel: () => void;
}

/**
 * PasswordConfirm
 *
 * there is no modal system in this project and window.confirm is out of the
 * question: it is unstyled, untranslatable and it cannot ask for a password.
 * so every destructive action opens this block right underneath its own button,
 * asks for the current password and only then runs.
 *
 * the password lives in here and nowhere else, and it is dropped the moment the
 * block closes - the parent never sees it after the call it asked for.
 */
const PasswordConfirm: React.FC<PasswordConfirmProps> = ({
  open,
  id,
  question,
  confirmLabel,
  requirePassword = true,
  busy = false,
  onConfirm,
  onCancel
}) => {
  const { I18N } = useContext(AppContext);
  const [ password, setPassword ] = useState('');

  /**
   * closing has to clear the field as well, not only unmounting: react keeps the
   * state of a component that merely stops rendering its children
   */
  useEffect(() => {
    if (!open) {
      setPassword('');
    }
  }, [open]);

  if (!open) {
    return null;
  }

  /**
   * handleSubmit
   * @param event
   */
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (busy) {
      return;
    }

    onConfirm(password);
  };

  return (
    <div data-password-confirm role="group" aria-label={ question }>
      <dia-form>
        <form onSubmit={ handleSubmit }>
          <dia-form-area>
            <dia-form-line>
              <p data-password-confirm-question>{ question }</p>
            </dia-form-line>
            { requirePassword && (
              <dia-form-line>
                <dia-form-control>
                  <input
                    type="password"
                    id={ id }
                    name={ id }
                    autoComplete="current-password"
                    placeholder={ I18N.t("Geben Sie Ihr Passwort ein") }
                    value={ password }
                    onChange={ (event) => setPassword(event.target.value) }
                    required
                  />
                  <label htmlFor={ id }>{ I18N.t("Aktuelles Passwort") }</label>
                </dia-form-control>
              </dia-form-line>
            ) }
            <dia-form-line>
              <dia-form-control>
                <button type="submit" data-primary-dark data-password-confirm-submit disabled={ busy }>
                  { confirmLabel }
                </button>
              </dia-form-control>
              <dia-form-control>
                <button type="button" data-secondary onClick={ onCancel } disabled={ busy }>
                  { I18N.t("Abbrechen") }
                </button>
              </dia-form-control>
            </dia-form-line>
          </dia-form-area>
        </form>
      </dia-form>
    </div>
  );
};

export default PasswordConfirm;
